import ErrorIcon from "@mui/icons-material/Error";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import Styles from "../../styles/_global/Styles";

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState(""); // State to store login errors
  const navigate = useNavigate();
  const { setAuth } = useContext(AuthContext); // Using AuthContext to manage global auth state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailInputRef = useRef(null); // Reference for the email input field

  useEffect(() => {
    // Focus the email input field when the component mounts
    emailInputRef.current && emailInputRef.current.focus();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(""); // Clear previous errors
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const res = await axios.post(`${backendUrl}/signin`, formData);
      localStorage.setItem("authToken", res.data.token); // Store the token in localStorage
      localStorage.setItem("user", JSON.stringify(res.data.user)); // Store the user object in localStorage
      setAuth(true); // Update the AuthContext
      navigate("/dashboard"); // Redirect to dashboard
    } catch (err) {
      setErrorMessage(err.response ? err.response.data.error : "Server Error");
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        minWidth: "100vw",
        backgroundColor: "#f4f4f4",
        backgroundImage: 'url("/assets/bg-repeat-01.png")',
        backgroundRepeat: "repeat",
      }}
    >
      <img
        src="/assets/logo-dark.svg"
        alt="Logo"
        style={{ marginBottom: 20, width: 200 }}
      />

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 400,
          boxShadow:
            "0px 4px 15px rgba(0, 0, 0, 0.05), 0px 2px 6px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography component="h1" variant="h5" gutterBottom>
            Sign In
          </Typography>

          {/* Error Message */}
          {errorMessage && ( // Display error message if it exists
            <Alert
              icon={<ErrorIcon fontSize="inherit" />}
              severity="error"
              sx={{ marginBottom: 2, width: "100%" }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email Address"
                  fullWidth
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  type="email"
                  variant="outlined"
                  InputProps={{
                    style: { borderRadius: 8 },
                  }}
                  inputRef={emailInputRef} // Attach the reference to the input
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  InputProps={{
                    style: { borderRadius: 8 },
                  }}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                marginTop: 3,
                marginBottom: 2,
                padding: "10px 0",
                borderRadius: 8,
                backgroundColor: Styles.primary.main,
                "&:hover": {
                  backgroundColor: Styles.secondary.main,
                },
                textTransform: "none",
                color: "#fff",
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing in..." : "Sign In"}
            </Button>
            <Grid container justifyContent="center">
              <Grid item>
                <Link
                  onClick={() => navigate("/forgot-password")}
                  variant="body2"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    fontWeight: "500",
                    color: Styles.primary.main,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Grid>
            </Grid>
            <Grid container justifyContent="center" sx={{ marginTop: 2 }}>
              <Grid item>
                <Link
                  component="button"
                  onClick={() => navigate("/register")}
                  variant="body2"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    fontWeight: "500",
                    color: Styles.primary.main,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Don't have an account? <strong>Register</strong>
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SignIn;
